import { useCallback, useRef } from 'react';
import type { OnboardingApiClient } from '@/features/onboarding/api/onboarding-api-client';
import { getEnrolledFactors } from '@/features/onboarding/hooks/onboarding-utils';
import {
  FlowStatus,
  FactorType,
  type OnboardingState,
  type OnboardingFlowResponse,
} from '@/features/onboarding/types/onboarding.types';

interface UseOnboardingResponseHandlersParams {
  apiClient: OnboardingApiClient;
  exchangeToken: OnboardingState['exchangeToken'];
  setState: React.Dispatch<React.SetStateAction<OnboardingState>>;
}

interface UseOnboardingResponseHandlersReturn {
  callCompleteApi: () => Promise<string | null>;
  processResponse: (response: OnboardingFlowResponse) => Promise<void>;
}

export function useOnboardingResponseHandlers({
  apiClient,
  exchangeToken,
  setState,
}: UseOnboardingResponseHandlersParams): UseOnboardingResponseHandlersReturn {
  const completePromiseRef = useRef<Promise<string | null> | null>(null);
  const hasCalledCompleteRef = useRef(false);

  // ============================================================================
  // COMPLETE API (centralized)
  // ============================================================================

  /**
   * Centralized /complete API call - ensures only one call is made.
   * Multiple callers will await the same promise.
   * Returns the exchange token on success, null on error (allowing retry).
   */
  const callCompleteApi = useCallback(async (): Promise<string | null> => {
    // Already completed - return cached token
    if (hasCalledCompleteRef.current) {
      setState(prev => ({
        ...prev,
        flowStatus: FlowStatus.COMPLETED,
        isLoading: false,
      }));

      return exchangeToken;
    }

    // If call is already in progress, wait for it
    if (completePromiseRef.current !== null) {
      return completePromiseRef.current;
    }

    // Start the API call and store the promise
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    completePromiseRef.current = (async () => {
      try {
        const response = await apiClient.completeOnboarding();
        const token = response.exchangeToken ?? null;

        setState(prev => ({
          ...prev,
          flowStatus: FlowStatus.COMPLETED,
          exchangeToken: token ?? prev.exchangeToken,
          isLoading: false,
          error: null,
        }));

        hasCalledCompleteRef.current = true;
        completePromiseRef.current = null;

        return token;
      } catch (error) {
        completePromiseRef.current = null;

        setState(prev => ({
          ...prev,
          error:
            error instanceof Error
              ? error.message
              : 'Failed to complete onboarding',
          isLoading: false,
          // Don't transition to COMPLETED on error - stay in current state for retry
        }));

        return null;
      }
    })();

    return completePromiseRef.current;
  }, [apiClient, exchangeToken, setState]);

  // ============================================================================
  // PROCESS BACKEND RESPONSE
  // ============================================================================

  /**
   * Process backend response and update state
   * This is the central place where we map backend response to frontend state
   *
   * IMPORTANT: When hasAlternative=true, this automatically calls the backend
   * /authenticator-alternatives API to fetch the list of options
   */
  const processResponse = useCallback(
    async (response: OnboardingFlowResponse) => {
      const factorType = response.currentStep?.factor_type ?? null;
      const responseFactors = response.factors ?? [];
      const newEnrolledFactors = getEnrolledFactors(responseFactors);

      // When hasAlternative=true, fetch alternatives from backend
      let alternatives = response.alternatives ?? [];

      if (response.hasAlternative && alternatives.length === 0) {
        try {
          const altResponse = await apiClient.getAuthenticatorAlternatives();
          alternatives = altResponse.alternatives.map(alt => ({
            factorType: alt.factorType as FactorType, // Backend already returns factorType
            isEnrolled: alt.isEnrolled,
            displayName: alt.displayName,
            description: alt.description,
          }));
        } catch (error) {
          // Log error but don't fail the flow - alternatives might be provided in response
          console.error('Failed to fetch authenticator alternatives:', error);
        }
      }

      setState(prev => {
        // Preserve enrolled factors if the new response doesn't include them
        // This handles cases where /complete returns minimal policy data
        const enrolledFactors =
          newEnrolledFactors.length > 0
            ? newEnrolledFactors
            : prev.enrolledFactors;

        return {
          ...prev,
          hasStarted: true,
          flowStatus: response.status,
          hasAlternative: response.hasAlternative,
          currentFactorType: factorType,
          currentStepMetadata: response.currentStep?.metadata ?? null,
          factors: responseFactors.length > 0 ? responseFactors : prev.factors,
          canComplete: response.canComplete ?? false,
          alternatives,
          enrolledFactors,
          // Extract phone number from metadata if present
          phoneNumberObfuscated:
            response.currentStep?.metadata?.phone_number_obfuscated ??
            prev.phoneNumberObfuscated,
          // Track primary factor type
          primaryFactorType:
            factorType === FactorType.PASSWORD
              ? FactorType.PASSWORD
              : prev.primaryFactorType,
          // Capture exchange token when flow completes
          exchangeToken: response.exchangeToken ?? null,
          isLoading: false,
          error: null,
          // Reset retry state on successful response to prevent stale lockout
          hasExceededRetries: false,
        };
      });
    },
    [apiClient, setState]
  );

  return { callCompleteApi, processResponse };
}
